import { useRef, useState, useEffect } from "react";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { formatPrice } from "@/utils/currency";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useWhatsAppStatus } from "@/contexts/WhatsAppStatusContext";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { ModifierSelectionModal } from "@/components/pos/ModifierSelectionModal";
import { useNewOrder, SOURCES, PLATFORMS, PHONE_PATTERNS, CARD_BRANDS } from "@/hooks/useNewOrder";
import { OrderSection } from "@/components/new-order/OrderSection";
import { OrderSuccessScreen } from "@/components/new-order/OrderSuccessScreen";
import { OrderCartSummary } from "@/components/new-order/OrderCartSummary";
import {
  Phone, MessageCircle, Store, Globe, Truck, ShoppingBag, ChefHat,
  Search, X, Plus, Tag, FileText, CreditCard, Banknote, Wallet,
  ChevronRight, Loader2, MapPin, Check, AlertCircle, Grid3X3, ArrowLeft, Coffee, CalendarClock
} from "lucide-react";
import { resolveMedia } from "@/utils/mediaResolver";
import { API_URL } from "@/utils/apiConfig";
import { WABanner } from "@/components/layout/WABanner";

const BACKEND_URL = API_URL;

const SOURCE_ICONS = { phone: Phone, whatsapp: MessageCircle, counter: Store, platform: Globe };
const DELIVERY_OPTIONS = [
  { key: "delivery", icon: Truck, desc: "Envío a domicilio" },
  { key: "pickup", icon: ShoppingBag, desc: "El cliente recoge" },
  { key: "dineIn", icon: ChefHat, desc: "Consumo en el local" },
  { key: "counter", icon: Store, desc: "Pedido no Balcão" },
];
const PAYMENT_OPTIONS = [
  { key: "fiado", label: "Fiado", icon: Wallet, desc: "Pago diferido" },
  { key: "online", label: "Pago online", icon: CreditCard, desc: "Redirige a checkout" },
  { key: "card", label: "Pago presencial", icon: Banknote, desc: "Efectivo o tarjeta" },
];

export default function NewOrderPage() {
  const { loading: authLoading, authAxios } = useAuth();
  const waConnected = useWhatsAppStatus();
  const searchRef = useRef(null);
  const {
    locale, localeCode,
    dataLoading, step, setStep,
    source, setSource, platform, setPlatform,
    customerPhone, setCustomerPhone, customerName, setCustomerName, phoneValid,
    deliveryType, setDeliveryType, deliveryAddress, setDeliveryAddress,
    deliveryComplement, setDeliveryComplement,
    deliveryBlock, setDeliveryBlock,
    deliveryReference, setDeliveryReference,
    deliveryNotes, setDeliveryNotes,
    deliveryLat, setDeliveryLat, deliveryLng, setDeliveryLng,
    tableNumber, setTableNumber, tables,
    cart, productSearch, setProductSearch, showCategories, setShowCategories,
    selectedCategory, setSelectedCategory, filteredProducts, categories, products,
    discount, setDiscount, discountType, setDiscountType, discountReason, setDiscountReason,
    couponCode, setCouponCode, appliedCoupon, setAppliedCoupon,
    observations, setObservations, fiscalDoc, setFiscalDoc,
    foundCustomerPoints, setFoundCustomerPoints,
    pointsToRedeem, setPointsToRedeem, pointsDiscount, POINTS_REDEMPTION_RATE,
    paymentType, setPaymentType, cardBrand, setCardBrand,
    cashReceived, setCashReceived, changeAmount, isCashBrand,
    scheduleDate, setScheduleDate, scheduleTime, setScheduleTime,
    restaurantLat, restaurantLng, restaurantCity, restaurantCountry,
    restaurantPaymentMethods,
    submitting, orderCreated,
    subtotal, discountAmount, shipping, total,
    addToCart, addToCartWithModifiers, updateCartQty, removeFromCart, applyCoupon,
    createOrder, resetOrder,
    canProceedToStep2, canProceedToStep3, canProceedToStep4, canProceedToStep5,
  } = useNewOrder();

  const [lookingUpCustomer, setLookingUpCustomer] = useState(false);
  const [foundCustomer, setFoundCustomer] = useState(null);

  // Show WhatsApp offline warning after a successful order
  const prevOrderCreated = useRef(null);
  useEffect(() => {
    if (orderCreated && !prevOrderCreated.current && !waConnected) {
      toast.warning("Pedido criado, mas o WhatsApp está desconectado — o cliente não receberá confirmação automática.", { duration: 8000 });
    }
    prevOrderCreated.current = orderCreated;
  }, [orderCreated, waConnected]);

  // Phone lookup: auto-fill customer name when phone >= 9 digits
  useEffect(() => {
    const digits = customerPhone.replace(/\D/g, "");
    if (digits.length < 9) { setFoundCustomer(null); return; }
    setLookingUpCustomer(true);
    const timer = setTimeout(async () => {
      try {
        const res = await authAxios.get(`/customers?search=${encodeURIComponent(digits)}&limit=1&sort_by=last_order_at`);
        const list = res.data?.customers || [];
        if (list.length > 0) {
          setFoundCustomer(list[0]);
          if (!customerName.trim()) setCustomerName(list[0].name);
          setFoundCustomerPoints(list[0].points || 0);
          if (!deliveryAddress.trim() && list[0].last_delivery_address) {
            setDeliveryAddress(list[0].last_delivery_address);
          }
        } else { setFoundCustomer(null); setFoundCustomerPoints(0); setPointsToRedeem(0); }
      } catch { setFoundCustomer(null); }
      setLookingUpCustomer(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [customerPhone]);

  // Pre-fill from duplicate order
  useEffect(() => {
    const raw = localStorage.getItem("kaiso_prefill_order");
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      localStorage.removeItem("kaiso_prefill_order");
      if (data.source) { setSource(data.source); setStep(2); }
      if (data.customer_name) setCustomerName(data.customer_name);
      if (data.customer_phone) {
        const digits = data.customer_phone.replace(/\D/g, "");
        setCustomerPhone(digits.slice(-9));
      }
      if (data.delivery_type) { setDeliveryType(data.delivery_type); setStep(3); }
      if (data.delivery_address) setDeliveryAddress(data.delivery_address);
    } catch {}
  }, []);

  // Category modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Modifier modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productModifierGroups, setProductModifierGroups] = useState([]);
  const [showModifierModal, setShowModifierModal] = useState(false);
  const [loadingModifiers, setLoadingModifiers] = useState(false);

  const handleProductClick = async (product) => {
    if (product.modifier_group_ids?.length > 0 || product.has_variation) {
      setSelectedProduct(product);
      setShowModifierModal(true);
      setLoadingModifiers(true);
      try {
        const res = await authAxios.get(`/menu/products/${product.id}/modifier-groups`);
        setProductModifierGroups(res.data || []);
      } catch {
        addToCart(product);
        setShowModifierModal(false);
      }
      setLoadingModifiers(false);
    } else {
      addToCart(product);
    }
  };

  const handleModifierConfirm = (data) => {
    addToCartWithModifiers(data);
    setShowModifierModal(false);
    setSelectedProduct(null);
    setProductModifierGroups([]);
  };

  if (authLoading || dataLoading) return <LoadingScreen />;

  if (orderCreated) {
    return (
      <OrderSuccessScreen
        orderCreated={orderCreated}
        customerName={customerName}
        total={total}
        paymentType={paymentType}
        cardBrand={cardBrand}
        changeAmount={changeAmount}
        onNewOrder={resetOrder}
      />
    );
  }

  const phonePattern = PHONE_PATTERNS[localeCode];

  return (
    <>
    <WABanner />
    <div className="min-h-screen bg-gray-50" data-testid="new-order-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/favicon.ico" alt="" className="w-7 h-7 hidden sm:block" onError={e => e.target.style.display = "none"} />
            <h1 className="text-base sm:text-lg font-bold text-gray-900 uppercase tracking-wide">Nuevo Pedido</h1>
          </div>
          <div className="flex items-center gap-4">
            {cart.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-bold text-emerald-700">{cart.reduce((s, c) => s + c.quantity, 0)} items</span>
                <span className="text-sm font-bold text-emerald-800">{formatPrice(total)}</span>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = "/orders"; }} data-testid="close-new-order">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6 pb-20 lg:pb-6 flex flex-col lg:flex-row gap-4 sm:gap-6">
        {/* LEFT: Form Steps */}
        <div className="flex-1 space-y-4 sm:space-y-6 min-w-0">

          {/* STEP 1: Source */}
          <OrderSection num={1} title="Origen del pedido" active={step >= 1} done={!!source}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {SOURCES.map(s => {
                const Icon = SOURCE_ICONS[s.key];
                return (
                  <button
                    key={s.key}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${source === s.key ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                    onClick={() => { setSource(s.key); if (s.key !== "platform") setPlatform(""); if (step < 2) setStep(2); }}
                    data-testid={`source-${s.key}`}
                  >
                    <div className={`w-10 h-10 rounded-full ${s.color} flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{s.label}</span>
                  </button>
                );
              })}
            </div>
            {source === "platform" && (
              <div className="mt-3">
                <Label className="text-xs uppercase text-gray-500 font-semibold">Seleccionar plataforma</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {PLATFORMS.map(p => (
                    <button
                      key={p.key}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${platform === p.key ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-200 hover:border-gray-300"}`}
                      onClick={() => { setPlatform(p.key); if (step < 2) setStep(2); }}
                      data-testid={`platform-${p.key}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </OrderSection>

          {/* STEP 2: Customer */}
          <OrderSection num={2} title="Datos del cliente" active={step >= 2 && canProceedToStep2} done={canProceedToStep3}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">{locale.labels.phone} {source === "whatsapp" && <span className="text-red-500">*</span>}</Label>
                <div className="flex mt-1">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-100 text-sm font-bold text-gray-700 select-none" data-testid="phone-prefix">
                    {phonePattern?.flag} {phonePattern?.prefix || "+34"}
                  </span>
                  <div className="relative flex-1">
                    <Input
                      value={customerPhone}
                      onChange={e => setCustomerPhone(e.target.value.replace(/[^\d\s]/g, ""))}
                      placeholder={phonePattern?.example || "612 345 678"}
                      className={`rounded-l-none ${phoneValid === false ? "border-red-400 focus:ring-red-400" : phoneValid === true ? "border-emerald-400 focus:ring-emerald-400" : ""}`}
                      data-testid="customer-phone-input"
                    />
                    {phoneValid !== null && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2">
                        {phoneValid ? <Check className="w-4 h-4 text-emerald-500" /> : <AlertCircle className="w-4 h-4 text-red-400" />}
                      </span>
                    )}
                  </div>
                </div>
                {lookingUpCustomer && <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />A buscar cliente...</p>}
                {foundCustomer && !lookingUpCustomer && (
                  <div className="mt-1 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5">
                    <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1 flex-wrap">
                      <Check className="w-3.5 h-3.5 shrink-0" />
                      {foundCustomer.name}
                      <span className="text-emerald-600 font-normal">· {foundCustomer.total_orders} pedido(s)</span>
                      {foundCustomer.total_spent > 0 && <span className="text-emerald-600 font-normal">· {formatPrice(foundCustomer.total_spent, localeCode)} total</span>}
                      {foundCustomer.points > 0 && <span className="ml-auto flex items-center gap-0.5 text-amber-600 font-bold">⭐ {foundCustomer.points} pts</span>}
                    </p>
                    {foundCustomer.last_order_at && <p className="text-[10px] text-gray-400 mt-0.5">Último pedido: {new Date(foundCustomer.last_order_at).toLocaleDateString("pt-PT", { day:"2-digit", month:"2-digit", year:"2-digit" })}</p>}
                  </div>
                )}
                {source === "whatsapp" && phoneValid === false && customerPhone && (
                  <p className="text-xs text-red-500 mt-1">Formato: {phonePattern?.prefix} {phonePattern?.mask || "XXX XXX XXX"}</p>
                )}
              </div>
              <div>
                <Label className="text-sm font-medium">{locale.labels.name} <span className="text-red-500">*</span></Label>
                <Input
                  value={customerName}
                  onChange={e => { setCustomerName(e.target.value); if (e.target.value.trim() && step < 3) setStep(3); }}
                  placeholder="Nombre del cliente (obligatorio)"
                  className={`mt-1 ${!customerName.trim() && step >= 2 ? "border-red-300" : ""}`}
                  data-testid="customer-name-input"
                />
                {!customerName.trim() && step >= 2 && (
                  <p className="text-xs text-red-500 mt-1">El nombre del cliente es obligatorio</p>
                )}
              </div>
            </div>
            {step < 3 && canProceedToStep3 && (
              <Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={() => setStep(3)} data-testid="next-to-delivery">
                Continuar <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </OrderSection>

          {/* STEP 3: Delivery Type */}
          <OrderSection num={3} title="Tipo de entrega" active={step >= 3 && canProceedToStep3} done={canProceedToStep4}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {DELIVERY_OPTIONS.map(dt => (
                <button
                  key={dt.key}
                  className={`p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${deliveryType === dt.key ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                  onClick={() => { setDeliveryType(dt.key); if (dt.key !== "delivery" && dt.key !== "dineIn" && step < 4) setStep(4); }}
                  data-testid={`delivery-${dt.key}`}
                >
                  <dt.icon className={`w-6 h-6 mb-2 ${deliveryType === dt.key ? "text-emerald-600" : "text-gray-400"}`} />
                  <p className="text-sm font-medium">{locale.labels[dt.key] || dt.key}</p>
                  <p className="text-xs text-gray-400">{dt.desc}</p>
                </button>
              ))}
            </div>
            {deliveryType === "delivery" && (
              <div className="mt-4">
                <Label className="text-sm font-medium flex items-center gap-1"><MapPin className="w-4 h-4" /> {locale.labels.address} <span className="text-red-500">*</span></Label>
                <div className="mt-1">
                  <AddressAutocomplete
                    value={deliveryAddress}
                    onChange={(val) => { setDeliveryAddress(val); if (val && step < 4) setStep(4); }}
                    onSelect={({ address, lat, lng }) => { setDeliveryAddress(address); setDeliveryLat(lat); setDeliveryLng(lng); if (step < 4) setStep(4); }}
                    placeholder="Buscar dirección..."
                    restaurantLat={restaurantLat}
                    restaurantLng={restaurantLng}
                    restaurantCity={restaurantCity}
                    restaurantCountry={restaurantCountry}
                    data-testid="delivery-address-input"
                  />
                </div>
                {deliveryLat && deliveryLng && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Coordenadas: {deliveryLat.toFixed(4)}, {deliveryLng.toFixed(4)}
                  </p>
                )}
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-medium text-gray-600 mb-1 block">Andar / Apto / Porta</Label>
                    <Input
                      value={deliveryComplement}
                      onChange={e => setDeliveryComplement(e.target.value)}
                      placeholder="3º Dto, Apt 12..."
                      data-testid="delivery-complement-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-600 mb-1 block">Bloco / Escada</Label>
                    <Input
                      value={deliveryBlock}
                      onChange={e => setDeliveryBlock(e.target.value)}
                      placeholder="Bloco A, Esc. 2..."
                      data-testid="delivery-block-input"
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <Label className="text-xs font-medium text-gray-600 mb-1 block">Referência / Ponto de referência</Label>
                  <Input
                    value={deliveryReference}
                    onChange={e => setDeliveryReference(e.target.value)}
                    placeholder="Próx. ao supermercado, portão azul..."
                    data-testid="delivery-reference-input"
                  />
                </div>
                <div className="mt-2">
                  <Label className="text-xs font-medium text-gray-600 mb-1 block">Observações da entrega</Label>
                  <Input
                    value={deliveryNotes}
                    onChange={e => setDeliveryNotes(e.target.value)}
                    placeholder="Não tocar campainha, deixar na portaria..."
                    data-testid="delivery-notes-input"
                  />
                </div>
              </div>
            )}
            {deliveryType === "dineIn" && (
              <div className="mt-4">
                <Label className="text-sm font-medium">Seleccionar mesa</Label>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 mt-2">
                  {tables.map(t => (
                    <button
                      key={t.id}
                      className={`p-3 rounded-lg text-xs font-bold uppercase transition-all ${t.status === "occupied" ? "bg-red-500 text-white cursor-not-allowed" : tableNumber === String(t.number) ? "bg-emerald-600 text-white shadow-md" : "bg-green-500 text-white hover:bg-green-600"}`}
                      onClick={() => { if (t.status !== "occupied") { setTableNumber(String(t.number)); if (step < 4) setStep(4); } }}
                      disabled={t.status === "occupied"}
                      data-testid={`select-table-${t.number}`}
                    >
                      {t.name || `M${t.number}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Scheduling — show for delivery/pickup/counter, not dineIn */}
            {deliveryType && deliveryType !== "dineIn" && (
              <div className="mt-4 pt-4 border-t">
                <Label className="text-sm font-medium flex items-center gap-1.5 mb-3">
                  <CalendarClock className="w-4 h-4 text-blue-500" /> Quando?
                </Label>
                <div className="flex gap-2">
                  <button
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${scheduleDate === "asap" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}
                    onClick={() => { setScheduleDate("asap"); setScheduleTime(""); if (step < 4) setStep(4); }}
                    data-testid="schedule-asap"
                  >
                    ⚡ Agora
                  </button>
                  <button
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${scheduleDate !== "asap" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}
                    onClick={() => { if (scheduleDate === "asap") setScheduleDate(""); }}
                    data-testid="schedule-later"
                  >
                    📅 Agendar
                  </button>
                </div>
                {scheduleDate !== "asap" && (
                  <input
                    type="datetime-local"
                    className="mt-2 w-full rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}` : ""}
                    min={new Date().toISOString().slice(0, 16)}
                    onChange={e => {
                      const [d, t] = (e.target.value || "").split("T");
                      setScheduleDate(d || ""); setScheduleTime(t?.slice(0, 5) || "");
                      if (d && t && step < 4) setStep(4);
                    }}
                    data-testid="schedule-datetime"
                  />
                )}
              </div>
            )}
          </OrderSection>

          {/* STEP 4: Products */}
          <OrderSection num={4} title="Agregar productos" active={step >= 4 && canProceedToStep4} done={cart.length > 0}>
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  ref={searchRef}
                  value={productSearch}
                  onChange={e => { setProductSearch(e.target.value); setSelectedCategory(null); }}
                  placeholder="Buscar producto (ej: guioza, cerveza...)"
                  className="pl-9"
                  data-testid="product-search-input"
                />
                {productSearch && (
                  <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setProductSearch("")}>
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
              <Button
                variant="outline"
                className="border-emerald-300 text-emerald-700 shrink-0"
                onClick={() => setShowCategoryModal(true)}
                data-testid="toggle-categories-btn"
              >
                <Grid3X3 className="w-4 h-4 mr-1" /> Categorías
              </Button>
            </div>
            {/* Selected category badge */}
            {selectedCategory && (
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xs text-gray-500">Filtrando:</span>
                <button
                  className="flex items-center gap-1 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full"
                  onClick={() => setSelectedCategory(null)}
                  data-testid="clear-category-filter"
                >
                  {categories.find(c => c.id === selectedCategory)?.name}
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {/* Category modal */}
            {showCategoryModal && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowCategoryModal(false)} data-testid="category-modal">
                <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <p className="font-bold text-gray-800">Categorías</p>
                    <button onClick={() => setShowCategoryModal(false)} className="p-1 rounded-full hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
                  </div>
                  <div className="overflow-y-auto p-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {/* Todos */}
                      <button
                        onClick={() => { setSelectedCategory(null); setProductSearch(""); setShowCategoryModal(false); }}
                        className={`rounded-xl border-2 p-3 text-left transition-all hover:shadow-md ${!selectedCategory ? "border-emerald-500 bg-emerald-50" : "border-gray-100 bg-white hover:border-emerald-300"}`}
                        data-testid="cat-all"
                      >
                        <div className="w-full h-16 bg-gradient-to-br from-emerald-100 to-gray-100 rounded-lg mb-2 flex items-center justify-center">
                          <Grid3X3 className="w-7 h-7 text-emerald-400" />
                        </div>
                        <p className="font-bold text-gray-800 text-sm truncate">Todos</p>
                        <p className="text-xs text-gray-400">{filteredProducts.length} productos</p>
                      </button>
                      {categories.map(cat => {
                        const coverImg = cat.image_url
                          || products.find(p => p.category_id === cat.id && (p.pos_media_url || p.image_url))?.pos_media_url
                          || products.find(p => p.category_id === cat.id && p.image_url)?.image_url;
                        const count = products.filter(p => p.category_id === cat.id).length;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => { setSelectedCategory(cat.id); setProductSearch(""); setShowCategoryModal(false); }}
                            className={`rounded-xl border-2 text-left transition-all hover:shadow-md ${selectedCategory === cat.id ? "border-emerald-500 bg-emerald-50" : "border-gray-100 bg-white hover:border-emerald-300"}`}
                            data-testid={`cat-block-${cat.id.slice(-6)}`}
                          >
                            {coverImg ? (
                              <img src={resolveMedia(coverImg)} alt={cat.name} className="w-full h-16 object-cover rounded-t-xl" onError={e => { e.target.style.display = "none"; }} />
                            ) : (
                              <div className="w-full h-16 bg-gradient-to-br from-emerald-50 to-gray-100 rounded-t-xl flex items-center justify-center">
                                <Coffee className="w-7 h-7 text-emerald-300" />
                              </div>
                            )}
                            <div className="p-2">
                              <p className="font-bold text-gray-800 text-xs truncate">{cat.name}</p>
                              <p className="text-[10px] text-gray-400">{count} prods.</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Search mode: simple list */}
            {productSearch ? (
              <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="product-results">
                {filteredProducts.length > 0 ? filteredProducts.map(p => {
                  const inCart = cart.find(c => c.product_id === p.id);
                  return (
                    <button
                      key={p.id}
                      className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-all ${inCart ? "border-emerald-400 bg-emerald-50" : "border-gray-200 bg-white hover:border-emerald-300 hover:bg-gray-50"}`}
                      onClick={() => handleProductClick(p)}
                      data-testid={`product-result-${p.id.slice(-6)}`}
                    >
                      {(p.pos_media_url || p.image_url) ? (
                        <img src={p.pos_media_url || p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" onError={e => { e.target.style.display='none'; }} />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                          <ChefHat className="w-5 h-5 text-emerald-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{p.name}</p>
                        <p className="text-xs font-bold text-emerald-600">{formatPrice(p.price)}</p>
                      </div>
                      {inCart && <span className="shrink-0 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white">{inCart.quantity}</span>}
                    </button>
                  );
                }) : (
                  <div className="py-6 text-center text-gray-400 text-sm">No se encontraron productos para "{productSearch}"</div>
                )}
              </div>
            ) : selectedCategory ? (
            /* Category mode: grid */
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto" data-testid="product-results">
              {filteredProducts.length > 0 ? filteredProducts.map(p => {
                const inCart = cart.find(c => c.product_id === p.id);
                return (
                  <button
                    key={p.id}
                    className={`relative rounded-xl border-2 overflow-hidden text-left transition-all ${inCart ? "border-emerald-500 ring-2 ring-emerald-200" : "border-gray-200 hover:border-emerald-300"}`}
                    onClick={() => handleProductClick(p)}
                    data-testid={`product-result-${p.id.slice(-6)}`}
                  >
                    {inCart && (
                      <div className="absolute top-1 right-1 z-10 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-xs font-bold text-white shadow">{inCart.quantity}</div>
                    )}
                    {(p.pos_media_url || p.image_url) ? (
                      <img src={p.pos_media_url || p.image_url} alt="" className="w-full h-20 object-cover" onError={e => { e.target.style.display='none'; }} />
                    ) : (
                      <div className="w-full h-20 bg-gradient-to-br from-emerald-50 to-gray-100 flex items-center justify-center">
                        <ChefHat className="w-7 h-7 text-emerald-300" />
                      </div>
                    )}
                    <div className="p-1.5">
                      <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2">{p.name}</p>
                      <p className="text-xs font-bold text-emerald-600 mt-0.5">{formatPrice(p.price)}</p>
                    </div>
                  </button>
                );
              }) : (
                <div className="col-span-full p-6 text-center text-gray-400 text-sm">Sin productos en esta categoría</div>
              )}
            </div>
            ) : (
            /* Default state: no search, no category selected */
            <div className="py-8 text-center text-gray-400">
              <ChefHat className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Busque um produto ou selecione uma <strong className="text-emerald-600">Categoría</strong></p>
            </div>
            )}
            {/* Observations — always accessible when cart has items */}
            {cart.length > 0 && (
              <div className="mt-3 border-t pt-3">
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Observações / Notas</Label>
                <Textarea
                  value={observations}
                  onChange={e => setObservations(e.target.value)}
                  placeholder="Alergias, instruções, pedidos especiais..."
                  rows={2}
                  className="text-sm"
                  data-testid="observations-input"
                />
              </div>
            )}
            {cart.length > 0 && step < 5 && (
              <Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={() => setStep(5)} data-testid="next-to-extras">
                Continuar <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </OrderSection>

          {/* STEP 5: Extras */}
          <OrderSection num={5} title="Descuento y documento" active={step >= 5 && canProceedToStep5} done={step >= 6}>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium flex items-center gap-1"><Tag className="w-4 h-4" /> Descuento / Cupón</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={couponCode} onChange={e => setCouponCode(e.target.value)} placeholder="Código del cupón" className="flex-1" data-testid="coupon-code-input" />
                  <Button variant="outline" onClick={applyCoupon} disabled={!couponCode} data-testid="apply-coupon-btn">Aplicar</Button>
                </div>
                {appliedCoupon ? (
                  <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg">
                    <Check className="w-4 h-4" />
                    <span>Cupón "{appliedCoupon.code}": -{appliedCoupon.type === "percentage" ? `${appliedCoupon.value}%` : formatPrice(appliedCoupon.value)}</span>
                    <button onClick={() => { setAppliedCoupon(null); setCouponCode(""); }}><X className="w-3.5 h-3.5 ml-2" /></button>
                  </div>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex gap-1.5 items-center">
                      <button
                        onClick={() => setDiscountType("fixed")}
                        className={`text-xs px-2.5 py-1.5 rounded border font-semibold transition ${discountType === "fixed" ? "bg-emerald-100 border-emerald-400 text-emerald-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                      >€</button>
                      <button
                        onClick={() => setDiscountType("percent")}
                        className={`text-xs px-2.5 py-1.5 rounded border font-semibold transition ${discountType === "percent" ? "bg-emerald-100 border-emerald-400 text-emerald-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                      >%</button>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={discount}
                        onChange={e => setDiscount(e.target.value)}
                        placeholder={discountType === "percent" ? "0 %" : "0.00 €"}
                        className="w-36"
                        data-testid="manual-discount"
                      />
                    </div>
                    <Input
                      type="text"
                      value={discountReason}
                      onChange={e => setDiscountReason(e.target.value)}
                      placeholder="Motivo do desconto (opcional)"
                      className="w-full text-sm"
                      data-testid="discount-reason"
                    />
                  </div>
                )}
              </div>
              {/* Loyalty points redemption */}
              {foundCustomerPoints > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-amber-800">⭐ {foundCustomerPoints} pontos disponíveis</p>
                      <p className="text-xs text-amber-600">{POINTS_REDEMPTION_RATE} pontos = 1€ de desconto</p>
                    </div>
                    {pointsToRedeem > 0 ? (
                      <button onClick={() => setPointsToRedeem(0)} className="text-xs text-amber-600 hover:text-red-600 font-medium">✕ Remover</button>
                    ) : (
                      <button
                        onClick={() => setPointsToRedeem(Math.min(foundCustomerPoints, Math.floor(subtotal * POINTS_REDEMPTION_RATE)))}
                        className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-bold hover:bg-amber-700"
                      >
                        Usar todos
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max={foundCustomerPoints}
                      step={POINTS_REDEMPTION_RATE}
                      value={pointsToRedeem || ""}
                      onChange={e => setPointsToRedeem(Math.min(foundCustomerPoints, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder={`Pontos a usar (máx. ${foundCustomerPoints})`}
                      className="flex-1 h-9 text-sm"
                      data-testid="points-redeem-input"
                    />
                    {pointsDiscount > 0 && (
                      <span className="text-sm font-bold text-amber-700 shrink-0 bg-amber-100 px-2 py-1 rounded-lg">−{formatPrice(pointsDiscount)}</span>
                    )}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-sm font-medium flex items-center gap-1"><FileText className="w-4 h-4" /> Documento fiscal (NIF/CPF)</Label>
                <Input value={fiscalDoc} onChange={e => setFiscalDoc(e.target.value)} placeholder="Identificación para factura (opcional)" className="mt-1" data-testid="fiscal-doc-input" />
              </div>
            </div>
            <Button size="sm" className="mt-4 bg-emerald-600 hover:bg-emerald-700" onClick={() => setStep(6)} data-testid="next-to-payment">
              Continuar al pago <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </OrderSection>

          {/* STEP 6: Payment */}
          <OrderSection num={6} title="Forma de pago" active={step >= 6} done={!!paymentType}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
              {PAYMENT_OPTIONS.map(pm => (
                <button
                  key={pm.key}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${paymentType === pm.key ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                  onClick={() => setPaymentType(pm.key)}
                  data-testid={`payment-${pm.key}`}
                >
                  <pm.icon className={`w-6 h-6 mb-2 ${paymentType === pm.key ? "text-emerald-600" : "text-gray-400"}`} />
                  <p className="text-sm font-medium">{pm.label}</p>
                  <p className="text-xs text-gray-400">{pm.desc}</p>
                </button>
              ))}
            </div>
            {paymentType === "card" && (() => {
              // Use restaurant-configured payment methods if available, otherwise fall back to defaults
              const methods = restaurantPaymentMethods.length > 0
                ? restaurantPaymentMethods
                : [...CARD_BRANDS.map(b => ({ name: b.label, key: b.key, color: b.color })), { name: "Dinheiro / Efectivo", key: "cash" }];
              return (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase text-gray-500 font-semibold mb-2 block">Seleccionar método</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {methods.map(m => {
                        const key = m.key || m.name.toLowerCase().replace(/[\s/()]+/g, "_");
                        return (
                          <button
                            key={key}
                            className={`px-4 py-3 rounded-lg border-2 text-sm font-bold transition-all ${cardBrand === key ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 hover:border-gray-300 text-gray-700"}`}
                            onClick={() => { setCardBrand(key); setCashReceived(""); }}
                            data-testid={`brand-${key}`}
                          >
                            {m.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Troco para dinheiro */}
                  {isCashBrand && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                      <p className="text-sm font-bold text-amber-800">Pagamento em Dinheiro</p>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <Label className="text-xs text-amber-700 font-semibold mb-1 block">Valor recebido do cliente</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder={`Mín. ${formatPrice(total)}`}
                            value={cashReceived}
                            onChange={e => setCashReceived(e.target.value)}
                            className="h-10 text-lg font-bold border-amber-300 focus:border-amber-500"
                            data-testid="cash-received-input"
                          />
                        </div>
                        {changeAmount > 0 && (
                          <div className="text-right">
                            <p className="text-xs text-amber-700 font-semibold">Troco</p>
                            <p className="text-2xl font-black text-amber-600">{formatPrice(changeAmount)}</p>
                          </div>
                        )}
                      </div>
                      {cashReceived && parseFloat(cashReceived) < total && (
                        <p className="text-xs text-red-600 font-semibold">⚠ Valor insuficiente (faltam {formatPrice(total - parseFloat(cashReceived))})</p>
                      )}
                      {!cashReceived && (
                        <p className="text-xs text-amber-600">Deixa em branco se não precisas calcular troco</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </OrderSection>
        </div>

        {/* RIGHT: Cart Summary */}
        <OrderCartSummary
          source={source}
          platform={platform}
          customerName={customerName}
          deliveryType={deliveryType}
          tableNumber={tableNumber}
          cart={cart}
          subtotal={subtotal}
          discountAmount={discountAmount}
          pointsDiscount={pointsDiscount}
          shipping={shipping}
          total={total}
          paymentType={paymentType}
          cardBrand={cardBrand}
          submitting={submitting}
          updateCartQty={updateCartQty}
          removeFromCart={removeFromCart}
          createOrder={createOrder}
        />
      </div>

      {/* Mobile floating cart bar */}
      {cart.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-3 z-50" data-testid="mobile-cart-bar">
          <Button
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base font-bold gap-2"
            onClick={paymentType ? createOrder : () => document.querySelector('[data-testid="order-cart-summary"]')?.scrollIntoView({ behavior: "smooth" })}
            disabled={submitting || cart.length === 0 || !paymentType || (paymentType === "card" && !cardBrand)}
            data-testid="mobile-create-order-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                <ShoppingBag className="w-5 h-5" />
                {paymentType ? `Crear Pedido · ${formatPrice(total)}` : `${cart.reduce((s, c) => s + c.quantity, 0)} items · ${formatPrice(total)}`}
              </>
            )}
          </Button>
        </div>
      )}
    </div>

    <ModifierSelectionModal
      open={showModifierModal}
      onOpenChange={(open) => {
        setShowModifierModal(open);
        if (!open) { setSelectedProduct(null); setProductModifierGroups([]); }
      }}
      product={selectedProduct}
      modifierGroups={productModifierGroups}
      onConfirm={handleModifierConfirm}
      loading={loadingModifiers}
    />
    </>
  );
}
